import React, { useCallback, useMemo } from "react";
import { Entity } from "@common/types/entity";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useUser } from "@hooks/useUser";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useMobileBlockScreen } from "@hooks/useMobileView";
import { useLogout } from "@hooks/useLogout";
import { PageCoverLoader } from "@design-system/PageCoverLoader/PageCoverLoader";
import { useOrganization } from "@api/organization.api";
import { PlainModal } from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { LogoutIcon } from "@icons";
import { useManageSteps } from "../useManageSteps";
import { OnboardingLayout } from "../layout/OnboardingLayout";
import { FlowContext } from "../flowContext";
import { flowResolver } from "../flowResolver";
import { PrimaryFlowSteps } from "../flowSteps";
import { ConnectPrivateAccounts } from "../components/ConnectPrivateAccounts/ConnectPrivateAccounts";
import containerStyles from "../components/BaseOnboardingContainer.module.scss";
import { useEmailVerifiedTracking } from "../useEmailVerifiedTracking";
import { useStepContent } from "./useStepContent";

interface Props {
    entity: Entity;
    onFinish(): Promise<void>;
}

export const PrimaryFlow: React.FC<Props> = ({ entity, onFinish }) => {
    const user = useUser();
    const logout = useLogout();
    const workspace = useActiveWorkspace();
    const allFinancialAccounts = useFinancialAccounts();
    const isMobile = useMobileBlockScreen();

    const { data: organization } = useOrganization();

    useEmailVerifiedTracking();

    const { isFetched: billingStatusFetched, isSubscribed } =
        useBillingStatus();

    const context: FlowContext = useMemo<FlowContext>(
        () => ({
            user,
            entity,
            workspace,
            organization: organization ?? null,
            financialAccounts: allFinancialAccounts,
            isSubscribed,
            isMobile,
        }),
        [
            user,
            entity,
            workspace,
            allFinancialAccounts,
            organization,
            isMobile,
            isSubscribed,
        ],
    );

    const getInitialStep = useCallback(
        (): PrimaryFlowSteps => flowResolver.current(context),
        [context],
    );

    const { go, currentStep, nextStep, isExiting } =
        useManageSteps<PrimaryFlowSteps>(billingStatusFetched, getInitialStep);

    const { main, sidebar, connectPersonalReminder, handleReminderConfirmed } =
        useStepContent({
            currentStep,
            entity,
            onFinish,
            go,
        });

    if (!billingStatusFetched) {
        return <PageCoverLoader />;
    }

    return (
        <>
            <OnboardingLayout
                currentStep={currentStep}
                nextStep={nextStep}
                isExiting={isExiting}
                main={main}
                sidebar={sidebar}
            />

            <Tooltip content="Log out">
                <Button
                    variant="secondary"
                    size="lg"
                    className={containerStyles.close}
                    onClick={logout}
                    iconOnly
                    aria-label="Log out"
                >
                    <LogoutIcon />
                </Button>
            </Tooltip>
            <PlainModal
                show={connectPersonalReminder}
                onHide={handleReminderConfirmed}
                size="lg"
            >
                <ConnectPrivateAccounts onContinue={handleReminderConfirmed} />
            </PlainModal>
        </>
    );
};
